import { useEffect, useRef } from "react";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { useUserStatus } from "@hooks/useUserStatus";
import { withRecentWorkspaceId } from "./pickRecentWorkspaces";

/**
 * Moves the active workspace to the front of the recent-workspaces cache on
 * every visit, so the org nav's recent clients follow workspaces opened from
 * anywhere, not only from the picker menu.
 */
export const useRecordRecentWorkspace = () => {
    const activeWorkspaceId = useActiveWorkspace()?.id;
    const { data: userStatus } = useUserStatus();
    const [cachedRecentIds, setCachedRecentIds] = useOrganizationUserCache<
        string[]
    >(ORGANIZATION_USER_CACHE_KEYS.RECENT_WORKSPACES_CACHE_KEY);
    const isUserLoaded = userStatus !== undefined;
    // The cache is only read on mount, so a nav that stays mounted across
    // client switches builds each write on its own last one. The setter is
    // new whenever the organization or user changes the cache key.
    const lastWriteRef = useRef<{
        setIds: typeof setCachedRecentIds;
        recentIds: string[];
    }>(undefined);

    useEffect(() => {
        if (!activeWorkspaceId || !isUserLoaded) {
            return;
        }
        const lastWrite = lastWriteRef.current;
        const recentIds = withRecentWorkspaceId({
            recentIds:
                lastWrite?.setIds === setCachedRecentIds
                    ? lastWrite.recentIds
                    : (cachedRecentIds ?? []),
            workspaceId: activeWorkspaceId,
        });
        lastWriteRef.current = { setIds: setCachedRecentIds, recentIds };
        setCachedRecentIds(recentIds);
    }, [activeWorkspaceId, cachedRecentIds, isUserLoaded, setCachedRecentIds]);
};
